import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { rem } from 'polished';

import dropdownArrow from 'images/dropdown-arrow.png';
import { breakpoints } from 'theme';
import { AVAILABLE_LOKALISE_LANGUAGES } from 'utils/constants';

import { useI18next } from 'gatsby-plugin-react-i18next';
import NavigationColor from 'utils/constants/NavigationColor';

const Container = styled.div<{ isOpen: boolean }>`
  display: block;
  background-color: transparent;
  font-size: ${rem(16)};
  line-height: ${rem(24)};

  position: relative;

  @media (max-width: ${breakpoints.tablet}) {
    display: none;
  }
`;

const SelectedOption = styled.button<{
  variant: NavigationColor.LIGHT | NavigationColor.DARK;
}>`
  width: 100%;
  background-color: transparent;
  color: ${({ theme, variant }) =>
    variant === NavigationColor.DARK ? '#000' : theme.neutralPalette.white};

  font-size: ${rem(14)};
  line-height: ${rem(21)};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};

  display: flex;
  align-items: center;

  padding: 16px;

  background-image: url(${dropdownArrow});
  background-repeat: no-repeat;
  background-position: right;
`;

const LangFlag = styled.img`
  width: 20px;
  height: auto;
  margin-right: 6px;
`;

const OptionsList = styled.ul<{ isOpen: boolean }>`
  list-style: none;
  padding: 16px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 1px 2px 18px rgb(0 0 0 / 25%);

  transition: all 300ms ease-in-out;

  max-height: 260px;

  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};

  position: absolute;
  z-index: 2;
`;

const Option = styled.li`
  text-align: left;
  font-size: ${rem(14)};
  line-height: ${rem(21)};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  padding: 4px;
  border-radius: 6px;
  color: #000;
  background-color: #fff;

  display: flex;
  align-items: center;

  &:hover {
    cursor: pointer;
    background-color: #f1f1f1;
  }
`;

interface Props {
  variant: NavigationColor.LIGHT | NavigationColor.DARK;
}

const LanguageDropdown = ({ variant }: Props) => {
  const { changeLanguage, language } = useI18next();

  const [dropdownIsOpen, setDropdownIsOpen] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<string>(language);

  const selectedOptionFlag = AVAILABLE_LOKALISE_LANGUAGES.find(
    (lang) => lang.abbreviation === language,
  )?.flagIcon;

  const ref = useRef(null);

  const handleDropdownClick = () =>
    setDropdownIsOpen((previousState) => !previousState);

  const handleClickOutside = (event: any) => {
    if (ref.current && !ref.current.contains(event.target)) {
      setDropdownIsOpen(false);
    }
  };

  const handleLangClick = (lang: string) => {
    if (lang === language) return;

    changeLanguage(lang);
    setSelectedOption(lang);
    setDropdownIsOpen(false);
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  return (
    <Container ref={ref} isOpen={dropdownIsOpen}>
      <SelectedOption
        type="button"
        variant={variant}
        onClick={handleDropdownClick}
      >
        <LangFlag src={selectedOptionFlag} alt="" />
        {selectedOption.toLocaleUpperCase()}
      </SelectedOption>
      {dropdownIsOpen && (
        <div>
          <OptionsList isOpen={dropdownIsOpen}>
            {AVAILABLE_LOKALISE_LANGUAGES.map((lang) => (
              <Option
                onClick={() => handleLangClick(lang.abbreviation)}
                key={lang.abbreviation}
              >
                <LangFlag src={lang.flagIcon} alt="" />
                {lang.abbreviation.toLocaleUpperCase()}
              </Option>
            ))}
          </OptionsList>
        </div>
      )}
    </Container>
  );
};

export default LanguageDropdown;
